import type { KeyboardEvent } from 'react'
import { Icon } from './Icon'

interface YearNavigatorProps {
  year: number
  min: number
  max: number
  onChange: (year: number) => void
}

/**
 * `‹ 2026 ›` year stepper — same chevron look as the calendar toolbar's month
 * navigation. Buttons disable at the bounds; ←/→ step while focus is inside
 * the group. The label is a polite live region so screen readers hear the new
 * year.
 */
export function YearNavigator({ year, min, max, onChange }: YearNavigatorProps): JSX.Element {
  const canPrev = year > min
  const canNext = year < max

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'ArrowLeft' && canPrev) {
      e.preventDefault()
      onChange(year - 1)
    } else if (e.key === 'ArrowRight' && canNext) {
      e.preventDefault()
      onChange(year + 1)
    }
  }

  return (
    <div
      role="group"
      aria-label="Year"
      className="year-nav"
      data-testid="year-nav"
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="tc-navbtn"
        aria-label="Previous year"
        disabled={!canPrev}
        onClick={() => onChange(year - 1)}
      >
        <Icon name="chevron_left" size={18} aria-hidden="true" />
      </button>
      <span className="tc-month year-nav-label" aria-live="polite" data-testid="year-nav-label">
        {year}
      </span>
      <button
        type="button"
        className="tc-navbtn"
        aria-label="Next year"
        disabled={!canNext}
        onClick={() => onChange(year + 1)}
      >
        <Icon name="chevron_right" size={18} aria-hidden="true" />
      </button>
    </div>
  )
}
